import { Alert } from "@msuite/ui/alert";
import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { getIbanCheckIssue, IBAN_CHECK_ISSUE_TEXT } from "@msuite/utils/incoming-invoices";
import { formatIban } from "@msuite/utils/strings";
import type { IncomingInvoiceDetail } from "@msuite/utils/types";
import React from "react";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";

/** Props Interface */
interface IncomingInvoiceIbanNoticeProps {
	incomingInvoice: IncomingInvoiceDetail;
}

/**
 * An IBAN the invoice's payee does not hold yet — a first account as a warning, one beside the
 * contact's known accounts as suspected fraud, a partner's that none of its workspaces carries as
 * unknown. Shown above the checks while `iban-known` fails; the invoice cannot be approved or paid
 * until one of its approvers confirms the account with the supplier and adopts it into the
 * contact, or the partner adds it to its workspace and the check runs again.
 */
export function IncomingInvoiceIbanNotice({ incomingInvoice }: IncomingInvoiceIbanNoticeProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const { user } = useAuth();
	const confirmModal = useAsyncModal();

	/** Functions */
	async function handleOnConfirm() {
		if (!isPartner && !(await confirmModal.confirm())) return;
		const response = await client.incoming_invoices[":incomingInvoiceId"]["bank-account"].$post({
			param: { incomingInvoiceId: incomingInvoice.id },
		});
		await responseHandler(response);
		await qc.invalidateQueries({
			predicate: invalidate(["incoming-invoices", "incoming-invoice-approvals-mine", "contact"]),
		});
	}

	/** Computed */
	const issue = getIbanCheckIssue(incomingInvoice.checks);
	const isFraud = issue?.kind === "suspected-fraud";
	// A partner keeps its accounts on its own workspaces, so its IBAN is checked again rather than
	// confirmed — which anyone reading the invoice may ask for.
	const isPartner = issue?.kind === "unknown-partner-iban";
	// The one who vouches for the account is the one who approves the payment.
	const mayConfirm =
		isPartner || incomingInvoice.approvals.some((approval) => approval.user.id === user?.id);

	/** Guard */
	if (!issue) return null;

	/** Render */
	return (
		<React.Fragment>
			<div className="v-stack gap-3">
				<Alert
					variant={issue.kind === "new-iban" ? "warning" : "danger"}
					title={t(IBAN_CHECK_ISSUE_TEXT[issue.kind].title, {
						iban: formatIban(issue.iban ?? incomingInvoice.iban),
					})}
					description={t(IBAN_CHECK_ISSUE_TEXT[issue.kind].description)}
				/>
				{mayConfirm && (
					<div className="h-stack gap-2 justify-end">
						<Button
							size="sm"
							variant={isFraud ? "danger-ghost" : "white"}
							icon={<Icon name={isPartner ? "RefreshCw" : "Landmark"} />}
							onClick={handleOnConfirm}
							data-cy="confirm-incoming-invoice-iban-button"
						>
							{t(
								isPartner
									? "incoming_invoices.iban_check.recheck"
									: "incoming_invoices.iban_check.confirm",
							)}
						</Button>
					</div>
				)}
			</div>
			{confirmModal.isOpen && (
				<ConfirmModal
					title={t("incoming_invoices.iban_check.confirm")}
					message={t("incoming_invoices.iban_check.confirm_message")}
					controller={confirmModal}
				/>
			)}
		</React.Fragment>
	);
}
